<script setup lang="ts">
import { onMounted, ref, h } from 'vue'
import {
  NCard, NButton, NSpace, NDataTable, NInput, NModal, NText, NAlert, NPopconfirm,
  type DataTableColumns,
} from 'naive-ui'
import { tokens, type Token } from '../../api'
import { message } from '../../message'

const rows = ref<Token[]>([])
const loading = ref(false)
const newName = ref('')
const plaintext = ref('')
const showPlaintext = ref(false)

async function load() {
  loading.value = true
  try { rows.value = await tokens.list() }
  catch (e: any) { message.error(e.message) }
  finally { loading.value = false }
}
onMounted(load)

async function create() {
  if (!newName.value.trim()) { message.error('name required'); return }
  try {
    const out = await tokens.create(newName.value.trim())
    plaintext.value = out.token
    showPlaintext.value = true
    newName.value = ''
    await load()
  } catch (e: any) { message.error(e.message) }
}

async function remove(id: number) {
  try { await tokens.delete(id); await load() }
  catch (e: any) { message.error(e.message) }
}

const cols: DataTableColumns<Token> = [
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Name', key: 'name' },
  {
    title: 'Created', key: 'created_at',
    render: (r) => new Date(r.created_at * 1000).toLocaleString(),
  },
  {
    title: 'Last used', key: 'last_used_at',
    render: (r) => r.last_used_at ? new Date(r.last_used_at * 1000).toLocaleString() : '—',
  },
  {
    title: '', key: 'actions', width: 100,
    render(r) {
      return h(NPopconfirm, { onPositiveClick: () => remove(r.id) }, {
        default: () => `Delete token "${r.name}"?`,
        trigger: () => h(NButton, { size: 'small', type: 'error', secondary: true }, () => 'Delete'),
      })
    },
  },
]
</script>

<template>
  <NAlert type="info" show-icon style="margin-bottom:16px">
    <template #header>Webhook Tokens（CI 回调用的访问凭证）</template>
    这些 token 用于 CI（如 GitHub Actions）调用 <code>/api/webhooks/image</code> 上报新构建的镜像。sm 只保存 token 的 sha256，明文<b>仅在创建时显示一次</b>，请立刻复制到 GitHub Secrets。删除 token 后对应的 CI 会立刻失效。
  </NAlert>

  <NCard>
    <template #header>Webhook tokens</template>
    <NSpace style="margin-bottom: 16px" align="center">
      <NInput v-model:value="newName" placeholder="为这个 token 起个名字，如 ci-blog" style="width: 280px" />
      <NButton type="primary" @click="create">+ 新建 Token（创建并显示明文）</NButton>
    </NSpace>
    <NDataTable :columns="cols" :data="rows" :loading="loading" />
  </NCard>

  <NModal v-model:show="showPlaintext" preset="card" title="新 Token（仅显示一次）" style="max-width:560px">
    <NAlert type="warning" show-icon style="margin-bottom:12px">
      此 token 明文<strong>只显示这一次</strong>。请立刻复制并保存到 GitHub Secrets（<code>SM_WEBHOOK_TOKEN</code>）。关闭对话框后无法再次获取，只能删除并重建。
    </NAlert>
    <NText code copyable>{{ plaintext }}</NText>
    <template #footer>
      <NButton @click="showPlaintext = false">我已保存，关闭</NButton>
    </template>
  </NModal>
</template>
